import { getBalance } from '@wagmi/core'
import { NetworkType } from 'abis/set/sets'
import Big from 'big.js'
import { CurrencyMetadata } from 'indices-list'
import { atom } from 'jotai'
import { networkToChain, wagmiConfig } from 'services/wallet/chains'
import { formatWeiToAmount } from 'utils'
import { Address, zeroAddress } from 'viem'
import { createAtomsWithQuery } from '../atomic-factory'
import { QUERY_KEY_USER_BALANCE } from '../keys'
import { BALANCE_REFETCH_INTERVAL, DEFAULT_REFETCH_INTERVAL } from '../refetch-intervals'
import { defaultCurrency } from '../zero-ex-requests/quotes/model'
import { PriceRequestAtoms, emptyBalance } from './model'

export type BalanceStorage = {
  [account: Address]: {
    [chainId: number]: {
      [asset: Address]: Big
    }
  }
}

type BalanceUpdateProps = {
  asset: CurrencyMetadata
  chainId: NetworkType
  userAddress: Address
  amount: Big
}

export const balanceStorageAtom = atom<BalanceStorage>({})

export const balanceSetterAtom = atom(null, (get, set, balanceUpdate: BalanceUpdateProps) => {
  const balances = get(balanceStorageAtom)
  const { userAddress, amount, asset, chainId } = balanceUpdate

  const chainIds = balances[userAddress]

  const balanceUpdater = {
    ...balances,
    [userAddress]: {
      ...chainIds,
      [chainId]: {
        ...(chainIds !== undefined ? chainIds[chainId] : []),
        [asset.address]: amount,
      },
    },
  }
  set(balanceStorageAtom, balanceUpdater)
})

const userAssetBalanceQuery = async <T>({
  queryKey: [, userAddress, selectedCurrency],
}: {
  queryKey: [string, Address, CurrencyMetadata]
}) => {
  return balanceQuery(userAddress, selectedCurrency) as T
}

export const assetBalanceQueryAtomsFactory = () => {
  const userAddressAtom = atom<Address>(zeroAddress)
  const currentAsset = atom<CurrencyMetadata>(defaultCurrency)

  const mainProperties = createAtomsWithQuery<
    Big,
    Address,
    CurrencyMetadata,
    null,
    [string, Address, CurrencyMetadata]
  >(
    { first: userAddressAtom, second: currentAsset },
    QUERY_KEY_USER_BALANCE,
    userAssetBalanceQuery<Big>,
    DEFAULT_REFETCH_INTERVAL,
    'userAssetBalanceQuery',
    BALANCE_REFETCH_INTERVAL,
  )

  return {
    userAddress: userAddressAtom,
    currentAsset: currentAsset,
    ...mainProperties,
  } as PriceRequestAtoms
}

export const balanceQuery = async (userAddress: Address, currency: CurrencyMetadata) => {
  const token = currency.address !== zeroAddress ? currency.address : undefined
  if (userAddress === zeroAddress) {
    return emptyBalance
  } else {
    const response = await getBalance(wagmiConfig, {
      address: userAddress,
      chainId: networkToChain(currency.chainId).id,
      token,
    })
    return formatWeiToAmount(response.value, response.decimals)
  }
}
